import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChallengeSummaryResponse } from "@/api/challenge";
import { ClaimStatus } from "@/api/claims";
import { useClaimRewards } from "@/hooks/useClaimRewards";
import { useShareImage } from "@/hooks/useShareImage";
import {
  SummaryHeader,
  HeroBanner,
  PerformanceCard,
  ChallengeHighlights,
  SummaryCTAs,
  ClaimSuccessModal,
} from "./challenge-summary";

interface ChallengeSummaryProps {
  data: ChallengeSummaryResponse;
  userAvatar: string;
  onClose: () => void;
  onShare: () => void;
  /** Mock claim status for dev testing (bypasses API call) */
  mockClaimStatus?: ClaimStatus;
  /** Mock claim handler for dev testing (simulates claim without real transaction) */
  onMockClaim?: () => void;
}

/**
 * Challenge Summary Component
 * Displays comprehensive post-completion summary for a challenge participant
 * Adapts display based on whether user won or lost the challenge
 */
export function ChallengeSummary({
  data,
  userAvatar,
  onClose,
  onShare,
  mockClaimStatus,
  onMockClaim,
}: ChallengeSummaryProps) {
  const navigate = useNavigate();
  const [isMockClaiming, setIsMockClaiming] = useState(false);
  const [showClaimSuccessModal, setShowClaimSuccessModal] = useState(false);

  const { challenge, userResult, personalStats, clubStats, leaderboard } = data;
  const { isWinner } = userResult;

  // Claim rewards hook for winners (skip API call if mock status provided)
  const { claimData, isClaiming, submitClaim } = useClaimRewards(challenge.id);

  // Pre-generate share image data (memoized to avoid unnecessary regeneration)
  const shareData = useMemo(
    () => ({
      totalDistance: personalStats.totalDistance,
      totalCalories: personalStats.totalCalories,
      averagePace: personalStats.averagePace ?? "--:--",
      durationDays: challenge.durationDays,
      payoutAmount: userResult.payoutAmount,
    }),
    [
      personalStats.totalDistance,
      personalStats.totalCalories,
      personalStats.averagePace,
      challenge.durationDays,
      userResult.payoutAmount,
    ]
  );

  // Pre-generate share image in background (parallel loading)
  const { share, isSharing, isReady: isShareReady } = useShareImage(shareData);

  // Use mock claim status if provided (for dev testing), otherwise use real data
  const effectiveClaimStatus = mockClaimStatus ?? claimData?.claimStatus;

  const handleStartNewChallenge = () => {
    navigate("/challenges");
  };

  const handleClaim = async () => {
    // If mock claim handler is provided (dev mode), use it instead of real claim
    if (onMockClaim) {
      setIsMockClaiming(true);
      // Simulate a brief loading state
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onMockClaim();
      setIsMockClaiming(false);
      setShowClaimSuccessModal(true);
      return;
    }

    // Real claim flow
    const result = await submitClaim();
    if (result.success) {
      console.log("Claim successful:", result.txHash);
      setShowClaimSuccessModal(true);
    }
  };

  /**
   * Handle share button click
   * Uses pre-generated image from useShareImage hook for instant sharing
   */
  const handleShare = async () => {
    if (isSharing) return;

    const success = await share();

    // If share completely failed (not just cancelled), use fallback
    if (!success && !isShareReady) {
      onShare();
    }
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-[#0c1c0c] text-[#e7f4e7]">
      <SummaryHeader onClose={onClose} />

      <main className="flex-1 pb-32 flex flex-col gap-6">
        <HeroBanner
          challengeName={challenge.name}
          isWinner={isWinner}
          payoutAmount={userResult.payoutAmount}
          stakeAmount={challenge.stakeAmount}
        />

        <PerformanceCard
          userAvatar={userAvatar}
          isWinner={isWinner}
          daysCompleted={personalStats.daysCompleted}
          requiredDays={challenge.requiredDays}
          totalDistance={personalStats.totalDistance}
          totalDays={challenge.durationDays}
          averagePace={personalStats.averagePace}
          longestRun={personalStats.longestRun}
          totalCalories={personalStats.totalCalories}
        />

        <ChallengeHighlights
          finalRank={userResult.finalRank}
          totalParticipants={userResult.totalParticipants}
          clubTotalDistance={clubStats.totalDistance}
          closestRival={leaderboard.closestRival}
          isWinner={isWinner}
        />
      </main>

      <SummaryCTAs
        isWinner={isWinner}
        claimStatus={effectiveClaimStatus}
        payoutAmount={userResult.payoutAmount}
        onClaim={handleClaim}
        onStartNewChallenge={handleStartNewChallenge}
        onShare={handleShare}
        isClaiming={isClaiming || isMockClaiming}
        isSharing={isSharing}
        isShareReady={isShareReady}
      />

      <ClaimSuccessModal
        isOpen={showClaimSuccessModal}
        onClose={() => setShowClaimSuccessModal(false)}
        payoutAmount={userResult.payoutAmount}
        onShare={handleShare}
        isSharing={isSharing}
      />
    </div>
  );
}
